import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { FieldProvider } from './contexts/FieldContext';
import { RefereeProvider } from './contexts/RefereeContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { ThemeProvider } from './contexts/ThemeContext';
import GlobalStyles from './styles/GlobalStyles';

// Компоненты
import HudHeader from './components/hud/HudHeader';
import HudFooter from './components/hud/HudFooter';
import HudBackground from './components/hud/HudBackground';
import PrivateRoute from './components/common/PrivateRoute';
import AdminRoute from './components/common/AdminRoute';
import VerifiedRoute from './components/common/VerifiedRoute';
import FieldRoute from './components/common/FieldRoute';
import ScrollToTop from './components/common/ScrollToTop';
import GlobalScanLine from './components/common/GlobalScanLine';
import { TrialBanner } from './components/subscription';

// Страницы
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import EmailVerificationPage from './pages/EmailVerificationPage';
import RegisterFieldPage from './pages/RegisterFieldPage';
import FieldEmailVerificationPage from './pages/FieldEmailVerificationPage';
// FieldProfilePage removed - now using unified UnifiedFieldPage
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordCodePage from './pages/ResetPasswordCodePage';
import ResetPasswordNewPage from './pages/ResetPasswordNewPage';
import MissionsPage from './pages/MissionsPage';
import MissionDetailPage from './pages/MissionDetailPage';
import MapsPage from './pages/MapsPage';
import MapDetailPage from './pages/MapDetailPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import IntelPage from './pages/IntelPage';
import NewsDetailPage from './pages/NewsDetailPage';
import ProfilePage from './pages/ProfilePage';
import AdminPage from './pages/AdminPage';
import ClansPage from './pages/ClansPage';
import ClanDetailPage from './pages/ClanDetailPage';
import CreateClanPage from './pages/CreateClanPage';
import CommandosPage from './pages/CommandosPage';
import FieldsPage from './pages/FieldsPage';
import UnifiedFieldPage from './pages/UnifiedFieldPage';
import RefereeRegistrationPage from './pages/RefereeRegistrationPage';
import RefereeRegistrationSuccessPage from './pages/RefereeRegistrationSuccessPage';
import RefereeProfilePage from './pages/RefereeProfilePage';
import MainRefereeFieldManagePage from './pages/MainRefereeFieldManagePage';
import FieldMapDetailPage from './pages/FieldMapDetailPage';
import FieldAnalyticsPage from './pages/FieldAnalyticsPage';
import GameDetailPage from './pages/GameDetailPage';
import ReviewsPage from './pages/ReviewsPage';
import NotFoundPage from './pages/NotFoundPage';
import MapEditorPage from './pages/MapEditorPage';
import WaiverBuilderPage from './pages/WaiverBuilderPage';
import WaiverSigningPage from './pages/WaiverSigningPage';
import FieldManagementPage from './pages/FieldManagementPage';
import { useAuth } from './contexts/AuthContext';
import { useFieldAuth } from './contexts/FieldContext';

const AuthRedirect: React.FC = () => {
  const { isAdmin } = useAuth();
  const { isFieldAuthenticated } = useFieldAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Только перенаправляем, если мы не на странице поля
    if (isAdmin() && !location.pathname.startsWith('/field-')) {
      navigate('/admin');
    }
  }, [isAdmin, navigate, location.pathname]);

  return null;
};

const AppContent: React.FC = () => {
  const location = useLocation();
  const { isAdmin } = useAuth();
  const isAdminPage = location.pathname.startsWith('/admin');
  const isFullScreenPage = location.pathname.startsWith('/verify-email') ||
                        location.pathname.startsWith('/verify-field-email') ||
                        location.pathname.startsWith('/verify-referee-email') ||
                        location.pathname.startsWith('/referee-registration') ||
                        location.pathname.startsWith('/map-editor');
  const isAuthPage = location.pathname.startsWith('/login') || location.pathname.startsWith('/register');

  // Показываем HudHeader и HudFooter везде, кроме админки и страниц верификации
  const showHud = !isAdminPage && !isFullScreenPage;

  return (
    <>
      <GlobalStyles />
      <ScrollToTop />
      <GlobalScanLine />
      <AuthRedirect />
      {isFullScreenPage ? (
        <Routes>
          <Route path="/verify-email" element={<EmailVerificationPage />} />
          <Route path="/verify-field-email" element={<FieldEmailVerificationPage />} />
          <Route path="/verify-referee-email" element={<RefereeRegistrationPage />} />
          <Route path="/referee-registration-success" element={<RefereeRegistrationSuccessPage />} />
          <Route path="/map-editor/:mapId" element={<MapEditorPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      ) : showHud ? (
        <HudBackground>
          <TrialBanner />
          <HudHeader />
          <main style={{ 
            padding: '20px', 
            flex: 1,
            paddingBottom: '60px'
          }}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/operations" element={<MissionsPage />} />
              <Route path="/operations/:id" element={<MissionDetailPage />} />
              <Route path="/maps" element={
                <PrivateRoute>
                  <VerifiedRoute>
                    <MapsPage />
                  </VerifiedRoute>
                </PrivateRoute>
              } />
              <Route path="/maps/:id" element={
                <PrivateRoute>
                  <VerifiedRoute>
                    <MapDetailPage />
                  </VerifiedRoute>
                </PrivateRoute>
              } />
              <Route path="/clans" element={<ClansPage />} />
              <Route path="/clans/:id" element={<ClanDetailPage />} />
              <Route path="/clans/create" element={
                <PrivateRoute>
                  <VerifiedRoute>
                    <CreateClanPage />
                  </VerifiedRoute>
                </PrivateRoute>
              } />
              <Route path="/fields" element={<FieldsPage />} />
              <Route path="/fields/:fieldId" element={<UnifiedFieldPage />} />
              <Route path="/fields/:fieldId/maps/:mapId" element={<FieldMapDetailPage />} />
              <Route path="/fields/:fieldId/analytics" element={<FieldAnalyticsPage />} />
              <Route path="/commandos" element={<CommandosPage />} />
              <Route path="/commandos/:id" element={<ProfilePage />} />
              <Route path="/cart" element={
                <FieldRoute>
                  <CartPage />
                </FieldRoute>
              } />
              <Route path="/checkout" element={
                <FieldRoute>
                  <CheckoutPage />
                </FieldRoute>
              } />
              <Route path="/intel" element={<IntelPage />} />
              <Route path="/intel/:id" element={<NewsDetailPage />} />
              <Route path="/profile" element={
                <PrivateRoute>
                  <VerifiedRoute>
                    <ProfilePage />
                  </VerifiedRoute>
                </PrivateRoute>
              } />
              <Route path="/profile/:userId" element={<ProfilePage />} />
              <Route path="/missions/:gameType/:gameId" element={<GameDetailPage />} />
              <Route path="/reviews/:targetType/:targetId" element={<ReviewsPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/register-field" element={<RegisterFieldPage />} />
              {/* /field-profile route removed - using unified /fields/:fieldId page */}
              <Route path="/referee/profile" element={<RefereeProfilePage />} />
              <Route path="/referee/public/:refereeId" element={<RefereeProfilePage />} />
              <Route path="/referee/manage-field" element={<MainRefereeFieldManagePage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password-code" element={<ResetPasswordCodePage />} />
              <Route path="/reset-password-new" element={<ResetPasswordNewPage />} />

              {/* Waiver System Routes */}
              <Route path="/waiver/:fieldId" element={<WaiverSigningPage />} />
              <Route path="/field/waiver/builder" element={
                <FieldRoute>
                  <WaiverBuilderPage />
                </FieldRoute>
              } />
              <Route path="/field/waiver/builder/:id" element={
                <FieldRoute>
                  <WaiverBuilderPage />
                </FieldRoute>
              } />
              <Route path="/field/management" element={
                <FieldRoute>
                  <FieldManagementPage />
                </FieldRoute>
              } />

              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </main>
          <HudFooter />
        </HudBackground>
      ) : (
        <Routes>
          <Route path="/admin" element={
            <AdminRoute>
              <AdminPage />
            </AdminRoute>
          } />
          <Route path="/admin/*" element={
            <AdminRoute>
              <AdminPage />
            </AdminRoute>
          } />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      )}
    </>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <ThemeProvider>
        <AuthProvider>
          <FieldProvider>
            <RefereeProvider>
              <SubscriptionProvider>
                <CartProvider>
                  <AppContent />
                </CartProvider>
              </SubscriptionProvider>
            </RefereeProvider>
          </FieldProvider>
        </AuthProvider>
      </ThemeProvider>
    </Router>
  );
};

export default App;
